import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  BarChart3,
  Calendar,
  CheckCircle2,
  Clock,
  XCircle,
  RefreshCw,
  Users,
  Image as ImageIcon,
  FileText,
  TrendingUp,
  Activity,
  PieChart,
  ArrowUpRight
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const STATUS_CONFIG = {
  pending: { color: 'bg-amber-500', text: 'text-amber-700', bg: 'bg-amber-50', icon: Clock, label: 'Pending' },
  approved: { color: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50', icon: CheckCircle2, label: 'Approved' },
  rejected: { color: 'bg-red-500', text: 'text-red-700', bg: 'bg-red-50', icon: XCircle, label: 'Rejected' },
};

const AnalyticsDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/analytics`);
      setAnalytics(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="h-8 w-48 bg-muted rounded animate-pulse" />
          <div className="h-9 w-24 bg-muted rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array(4).fill(0).map((_, i) => <div key={i} className="h-28 bg-muted rounded-lg animate-pulse" />)}
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {Array(4).fill(0).map((_, i) => <div key={i} className="h-64 bg-muted rounded-lg animate-pulse" />)}
        </div>
      </div>
    );
  }

  if (!analytics) return <p className="text-muted-foreground">Failed to load analytics.</p>;

  const { activity_status, events_by_month, events_by_category, events_by_submitter, top_associations, signatures, media, recent_activities, constitution } = analytics;

  // Find max for bar chart scaling
  const monthEntries = Object.entries(events_by_month);
  const maxMonthCount = Math.max(...monthEntries.map(([, v]) => v), 1);

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-cameroon-green" />
            Analytics
          </h1>
          <p className="text-sm text-muted-foreground">Platform insights and metrics</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchAnalytics} data-testid="analytics-refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="analytics-summary-cards">
        <SummaryCard
          title="Total Events"
          value={activity_status.total}
          icon={Calendar}
          gradient="from-cameroon-green/10 to-cameroon-green/5"
          iconBg="bg-cameroon-green/20"
          iconColor="text-cameroon-green"
          testId="analytics-total-events"
        />
        <SummaryCard
          title="Approved"
          value={activity_status.approved}
          icon={CheckCircle2}
          gradient="from-emerald-500/10 to-emerald-500/5"
          iconBg="bg-emerald-500/20"
          iconColor="text-emerald-500"
          subtitle={activity_status.total > 0 ? `${Math.round((activity_status.approved / activity_status.total) * 100)}% approval rate` : ''}
          testId="analytics-approved-events"
        />
        <SummaryCard
          title="Signatures"
          value={`${signatures.signed}/${signatures.total}`}
          icon={FileText}
          gradient="from-cameroon-red/10 to-cameroon-red/5"
          iconBg="bg-cameroon-red/20"
          iconColor="text-cameroon-red"
          subtitle={constitution.compiled ? 'Compiled' : 'In Progress'}
          testId="analytics-signatures"
        />
        <SummaryCard
          title="Media Files"
          value={media.total}
          icon={ImageIcon}
          gradient="from-blue-500/10 to-blue-500/5"
          iconBg="bg-blue-500/20"
          iconColor="text-blue-500"
          subtitle={`${media.s3} S3 / ${media.local} local`}
          testId="analytics-media"
        />
      </div>

      {/* Charts Row */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Activity Status Breakdown */}
        <Card data-testid="analytics-status-breakdown">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <PieChart className="w-4 h-4 text-muted-foreground" />
              Event Status Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(STATUS_CONFIG).map(([status, config]) => {
                const count = activity_status[status] || 0;
                const pct = activity_status.total > 0 ? Math.round((count / activity_status.total) * 100) : 0;
                return (
                  <div key={status} className="flex items-center gap-3" data-testid={`analytics-status-${status}`}>
                    <config.icon className={`w-4 h-4 ${config.text}`} />
                    <span className="text-sm font-medium w-20">{config.label}</span>
                    <div className="flex-1 h-3 bg-muted rounded-full overflow-hidden">
                      <div className={`h-full ${config.color} rounded-full transition-all duration-700`} style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-sm font-semibold w-12 text-right">{count}</span>
                    <span className="text-xs text-muted-foreground w-10 text-right">{pct}%</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Events by Category */}
        <Card data-testid="analytics-events-by-category">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Activity className="w-4 h-4 text-muted-foreground" />
              Events by Category
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(events_by_category).length === 0 ? (
              <p className="text-sm text-muted-foreground">No events yet</p>
            ) : (
              <div className="space-y-2">
                {Object.entries(events_by_category)
                  .sort(([, a], [, b]) => b - a)
                  .map(([cat, count]) => (
                    <div key={cat} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors">
                      <span className="text-sm capitalize">{cat}</span>
                      <Badge variant="secondary" className="font-mono">{count}</Badge>
                    </div>
                  ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Events by Month Chart */}
      <Card data-testid="analytics-events-by-month">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-muted-foreground" />
            Approved Events by Month
          </CardTitle>
          <CardDescription>Distribution of approved events across the calendar year</CardDescription>
        </CardHeader>
        <CardContent>
          {monthEntries.length === 0 ? (
            <p className="text-sm text-muted-foreground">No approved events yet</p>
          ) : (
            <div className="flex items-end gap-2 h-40">
              {monthEntries.map(([monthKey, count]) => {
                const [, month] = monthKey.split('-');
                const monthIdx = parseInt(month, 10) - 1;
                const heightPct = (count / maxMonthCount) * 100;
                return (
                  <div key={monthKey} className="flex-1 flex flex-col items-center gap-1" data-testid={`analytics-month-${monthKey}`}>
                    <span className="text-xs font-semibold text-foreground">{count}</span>
                    <div className="w-full bg-muted rounded-t-md overflow-hidden" style={{ height: '120px' }}>
                      <div
                        className="w-full bg-gradient-to-t from-cameroon-green to-cameroon-green/70 rounded-t-md transition-all duration-700"
                        style={{ height: `${heightPct}%`, marginTop: `${100 - heightPct}%` }}
                      />
                    </div>
                    <span className="text-xs text-muted-foreground">{MONTH_NAMES[monthIdx] || monthKey}</span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bottom Row */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Top Associations */}
        <Card data-testid="analytics-top-associations">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Users className="w-4 h-4 text-muted-foreground" />
              Most Active Associations
            </CardTitle>
          </CardHeader>
          <CardContent>
            {top_associations.length === 0 ? (
              <p className="text-sm text-muted-foreground">No association events yet</p>
            ) : (
              <div className="space-y-2">
                {top_associations.map((assoc, idx) => (
                  <div key={assoc.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors" data-testid={`analytics-top-assoc-${idx}`}>
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      idx === 0 ? 'bg-cameroon-yellow/30 text-cameroon-yellow' :
                      idx === 1 ? 'bg-gray-200 text-gray-600' :
                      'bg-orange-100 text-orange-600'
                    }`}>
                      {idx + 1}
                    </span>
                    <span className="text-sm flex-1 truncate">{assoc.name}</span>
                    <Badge variant="outline" className="font-mono">{assoc.count} events</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Events by Submitter Type */}
        <Card data-testid="analytics-events-by-submitter">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <ArrowUpRight className="w-4 h-4 text-muted-foreground" />
              Events by Submitter Type
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(events_by_submitter).length === 0 ? (
              <p className="text-sm text-muted-foreground">No events yet</p>
            ) : (
              <div className="space-y-3">
                {Object.entries(events_by_submitter).map(([type, count]) => {
                  const totalSub = Object.values(events_by_submitter).reduce((a, b) => a + b, 0);
                  const pct = totalSub > 0 ? Math.round((count / totalSub) * 100) : 0;
                  const label = type === 'association' ? 'CCA Member' : type === 'organization' ? 'External Org' : type === 'individual' ? 'Individual' : type;
                  return (
                    <div key={type} className="space-y-1" data-testid={`analytics-submitter-${type}`}>
                      <div className="flex justify-between text-sm">
                        <span className="capitalize">{label}</span>
                        <span className="font-semibold">{count} ({pct}%)</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-cameroon-red to-cameroon-yellow rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity Feed */}
      <Card data-testid="analytics-recent-activity">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Clock className="w-4 h-4 text-muted-foreground" />
            Recent Submissions
          </CardTitle>
          <CardDescription>Latest event booking submissions</CardDescription>
        </CardHeader>
        <CardContent>
          {(!recent_activities || recent_activities.length === 0) ? (
            <p className="text-sm text-muted-foreground">No recent activity</p>
          ) : (
            <div className="space-y-2">
              {recent_activities.map((act, idx) => {
                const cfg = STATUS_CONFIG[act.status] || STATUS_CONFIG.pending;
                return (
                  <div key={act.id || idx} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors" data-testid={`analytics-recent-${idx}`}>
                    <cfg.icon className={`w-4 h-4 shrink-0 ${cfg.text}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{act.title}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {act.association_name || act.submitter_type || 'Unknown'}
                      </p>
                    </div>
                    <Badge className={`${cfg.bg} ${cfg.text} border-0 shrink-0 text-xs`}>
                      {cfg.label}
                    </Badge>
                    {act.created_at && (
                      <span className="text-xs text-muted-foreground shrink-0 hidden sm:block">
                        {new Date(act.created_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const SummaryCard = ({ title, value, icon: Icon, gradient, iconBg, iconColor, subtitle, testId }) => (
  <Card className={`bg-gradient-to-br ${gradient} border-transparent`} data-testid={testId}>
    <CardContent className="p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2.5 rounded-lg ${iconBg}`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
        <div>
          <p className="text-xs text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold leading-tight">{value}</p>
          {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
        </div>
      </div>
    </CardContent>
  </Card>
);

export default AnalyticsDashboard;
